import { CtaButton } from '@app/components/ui/CtaButton/CtaButton';
import { useTasks } from '@app/context/TaskContext';
import { useSnackbar } from '@app/hooks/useSnackbar';
import { pickLatest } from '@app/utils/pickLatest';
import { Timer } from '@components/block/Navigation/Timer';
import { Spinner } from '@components/ui';
import { UserFlowItem } from '@components/ui/UserFlowItem/UserFlowItem';
import { TaskType } from '@dieterApi/task/useTaskQuery';
import { ApplicationClass, UserApplicationQuestionnaires } from '@dieterApi/user/useUserQuery';
import AddBusinessIcon from '@mui/icons-material/AddBusiness';
import DoDisturbIcon from '@mui/icons-material/DoDisturb';
import EditIcon from '@mui/icons-material/Edit';
import LockIcon from '@mui/icons-material/Lock';
import SchoolIcon from '@mui/icons-material/School';
import ShareIcon from '@mui/icons-material/Share';
import { IconButton, Tooltip } from '@mui/material';
import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';
import { TaskWithApp } from '../../Tasks/TaskList';
import { Dashboard } from '../../useDashboard';

import { useNavigation } from '@app/hooks/useNavigation';
import { getTopicStates } from '@app/utils/getTopicStates';
import { NewBadge } from '@components/ui/NewBadge/NewBadge';
import { useTranslation } from 'react-i18next';
import { UserFlowItemType } from '../../constants';
import DocumentMenu from './DocumentMenu';
import { DuplicateModal } from './DuplicateModal';
import { RevisitBadge } from './RevisitBadge';
import './formitem.sass';

interface Props {
  dashboard: Dashboard;
  isFocusTask?: boolean;
  app: UserApplicationQuestionnaires;
  task?: TaskWithApp;
  index: number;
}

export function FormItem({ dashboard, app, index, task, isFocusTask }: Props) {
  const { t } = useTranslation();
  const { user, principalCompany, questionnaires, topics } = useUserWithCompany();
  const { userValues } = useUserValuesData();
  const { navigation, setNavigation } = useNavigation();
  const { enqueueSnackbar } = useSnackbar();
  const [getShareToken, { data: tokenData, loading }] = useQuestionnaireDocumentShareQuery();
  const tasks = useTasks();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const TOPIC_LOCKED_TEXT = t('dashboard.topic.forms.formitem.locked');

  const [duplicateModalOpen, setDuplicateModalOpen] = useState(false);
  const [hasCopied, setHasCopied] = useState(false);
  const [shouldCopyToken, setShouldCopyToken] = useState(false);

  // when the corresponding topic is disabled (when a user is not a subscriber), we want to open the CallToAction
  const topic = topics.find((topic) => topic.applications.map((app) => app.id).includes(app.id));
  // choose latest questionnaire
  const questionnaire = pickLatest(app.questionnaires);

  const split = app.splitApplication;
  const formProgress = (split ? questionnaire?.sectionProgress[index] : questionnaire?.progress) || 0;
  const revisit = task?.type === TaskType.REVISIT;
  const formFinished = formProgress === 1;
  const taskDone = task?.done || false;
  const formAndTaskFinished = formFinished && taskDone;
  const inProgress = formProgress > 0;
  // MH: I removed the mandatory existence of a task, for a form to be unlocked
  // This was incompatible with the direct onboarding route to DSE, AVV, etc.
  const formLocked = split && index > 0 && questionnaire?.sectionProgress[index - 1] !== 1; // || !task;
  const thisDuration = task?.duration || 5;
  const thisModifiedAt = (split ? questionnaire?.sectionModifiedAt[index] : questionnaire?.updatedAt) || '';

  const { isLocked } = topic ? getTopicStates(topic, user, userValues) : { isLocked: true };
  const isPublic = app.tags?.map((t) => t.tag).includes('public');
  const isLegacy = app.tags?.map((t) => t.tag).includes('legacy');
  const isBroken =
    (questionnaire?.isBroken && [ApplicationClass.Contract, ApplicationClass.Document].includes(app.class)) || false;
  const isDocument = app.class === ApplicationClass.Document;

  useEffect(() => {
    if (formFinished && !taskDone && !revisit) {
      // set task to done, when it has not happened yet
      task && task.setStatus(true);
    }
  }, [formAndTaskFinished, taskDone, revisit]);

  // Auto-copy to clipboard when token is loaded after user clicked share
  useEffect(() => {
    if (shouldCopyToken && tokenData?.getQuestionnaireShareToken && !loading) {
      const token = tokenData.getQuestionnaireShareToken;
      const url = window.location.origin + '/share?token=' + token;

      copyToClipboard(url)
        .then(() => {
          setHasCopied(true);
          if (navigation.isMobile) {
            enqueueSnackbar(t('dashboard.topic.process.publishprocess.copy_success'), { variant: 'success' });
          }
        })
        .catch((error) => {
          enqueueSnackbar(t('components.block.OutputView.ConsumerPanel.copyerror'), { variant: 'error' });
        })
        .finally(() => {
          setShouldCopyToken(false);
        });
    }
  }, [tokenData, loading, shouldCopyToken]);

  const handleButton = (section?: number, revisit: boolean = false) => {
    if (user?.isReadOnly && (!formFinished || revisit)) {
      enqueueSnackbar(t('common.readonly'), { variant: 'error' });
      return;
    }
    if (questionnaire) {
      dashboard.open(questionnaire.id, section, revisit);
    } else {
      dashboard.create(app.id, app.splitApplication ? 1 : undefined);
    }
  };

  // used to automatically open the form, when a user clicks on the new chat message notification related to this form
  useEffect(() => {
    const pendingAction = navigation.pendingNotificationOpen;
    if (!pendingAction || pendingAction.action !== 'open-chat') {
      return;
    }

    if (pendingAction.applicationId !== app.id) {
      return;
    }

    if (split) {
      const targetSection = pendingAction.section || 1;
      if (targetSection !== index + 1) {
        return;
      }
    } else if (index !== 0) {
      return;
    }

    const shouldUseShowAction = formFinished && !isBroken;
    setNavigation((nav) => {
      nav.pendingNotificationOpen = null;
      if (shouldUseShowAction) {
        nav.questAutoExpandChat = false;
      }
    });

    if (formLocked) {
      enqueueSnackbar(t('dashboard.topic.forms.formitem.firstfinishothers'), { variant: 'error' });
      return;
    }

    if (shouldUseShowAction) {
      openDocument(0);
      return;
    }

    handleButton(index + 1, formFinished);
  }, [
    navigation.pendingNotificationOpen,
    app.id,
    split,
    index,
    formLocked,
    formFinished,
    isBroken,
    setNavigation,
    enqueueSnackbar,
    t,
  ]);

  const handleDuplicate = () => {
    if (user?.isReadOnly) {
      enqueueSnackbar(t('common.readonly'), { variant: 'error' });
      return;
    }
    setDuplicateModalOpen(true);
  };

  const handleShow = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (app.documentLabels.length > 1) {
      setAnchorEl(event.currentTarget);
    } else {
      openDocument(0);
    }
  };

  const handleShare = () => {
    // If token already exists, copy immediately
    if (tokenData?.getQuestionnaireShareToken) {
      const url = window.location.origin + '/share?token=' + tokenData.getQuestionnaireShareToken;
      copyToClipboard(url)
        .then(() => {
          setHasCopied(true);
          if (navigation.isMobile) {
            enqueueSnackbar(t('dashboard.topic.process.publishprocess.copy_success'), { variant: 'success' });
          }
        })
        .catch((error) => {
          enqueueSnackbar(t('components.block.OutputView.ConsumerPanel.copyerror'), { variant: 'error' });
        });
    } else if (!loading) {
      // Fetch the token and mark that we should copy it when ready
      setShouldCopyToken(true);
      getShareToken({
        variables: {
          applicationId: app.id,
          type: 'UniqueQuestionnaire',
          withConfirmation: false,
        },
      });
    }
  };

  const openDocument = (index: number) => {
    if (isLocked && !isPublic) {
      if (topic) {
        if (topic.id === 'topic-dsfa') {
          navigation.roadBlockDSFA.open();
        } else {
          navigation.roadBlock.open(topic);
        }
      }
    } else {
      const q = questionnaires?.find((q) => q.id === questionnaire?.id);
      setNavigation((nav) => {
        nav.justFinishedQuestionnaireRemoteId = null;
        nav.preferredTab = undefined;
        nav.documentPaper = {
          modalOpen: true,
          questionnaire: q,
          index,
        };
      });
    }
  };

  const CTAMapStart: Record<ApplicationClass, string> = {
    [ApplicationClass.Analysis]: t('common.cta.start_analysis'),
    [ApplicationClass.Contract]: t('common.cta.create_contract'),
    [ApplicationClass.Document]: t('common.cta.create_document'),
    [ApplicationClass.Training]: t('common.cta.start_training'),
  };

  const CTAMapFinish: Record<ApplicationClass, string> = {
    [ApplicationClass.Analysis]: t('common.cta.finish_analysis'),
    [ApplicationClass.Contract]: t('common.cta.finish_contract'),
    [ApplicationClass.Document]: t('common.cta.finish_document'),
    [ApplicationClass.Training]: t('common.cta.finish_training'),
  };

  const CTAMapShow: Record<ApplicationClass, string> = {
    [ApplicationClass.Analysis]: t('common.cta.show_analysis'),
    [ApplicationClass.Contract]: t('common.cta.show_contract'),
    [ApplicationClass.Document]: t('common.cta.show_document'),
    [ApplicationClass.Training]: t('common.cta.show_training'),
  };

  const CTAMapRework: Record<ApplicationClass, string> = {
    [ApplicationClass.Analysis]: t('common.cta.rework_analysis'),
    [ApplicationClass.Contract]: t('common.cta.rework_contract'),
    [ApplicationClass.Document]: t('common.cta.rework_document'),
    [ApplicationClass.Training]: t('common.cta.rework_training'),
  };

  return (
    <div className="h-full">
      <UserFlowItem
        type={UserFlowItemType.questionnaire}
        title={t('common.section') + ': ' + topic?.title}
        progress={revisit ? 0.9 : formProgress}
        isFocusTask={isFocusTask}
        done={formFinished}
        broken={isBroken && formFinished}
        onIgnore={task?.setIgnore}
        isIgnored={task?.isIgnored}
        // isNew={task?.isNew && !inProgress}
        // isNewVersion={revisit}
        topRightElements={
          <div className="flex gap-3 items-center w-full place-content-end">
            {!revisit && thisModifiedAt && (
              <div className="text-xs grow">
                {thisModifiedAt &&
                  t('dashboard.topic.forms.formitem.lastchanged') +
                    DateTime.fromJSDate(new Date(thisModifiedAt))
                      .setLocale(user?.locale || 'en')
                      .toRelative()}
              </div>
            )}

            {!tasks ? (
              <Spinner size="small" />
            ) : formFinished ? (
              isBroken ? (
                <RevisitBadge />
              ) : isLegacy ? (
                <Tooltip title={t('dashboard.topic.forms.formitem.legacy')}>
                  <div className="flex items-center gap-1">
                    <span className="text-warning-500 font-medium brightness-75">{t('common.legacy')}</span>
                    <DoDisturbIcon className="text-warning-500" fontSize="small" />
                  </div>
                </Tooltip>
              ) : (
                <span className="text-success-500 font-medium brightness-75">{t('common.done')}</span>
              )
            ) : (
              <>
                {task?.isNew && <NewBadge />}
                <Timer duration={revisit ? 5 : thisDuration} />
              </>
            )}
          </div>
        }
        bodyElements={
          <div className="w-full flex flex-col gap-2 ">
            <div className="grow flex flex-col gap-2">
              {/* <span className="text-gray-400 text-[0.8em] leading-3">{t('common.section') + ': ' + topic?.title}</span> */}
              <div className="text-lg">
                <div>{app.sectionLabels[index]}</div>
              </div>
            </div>

            <div className="position-relative flex items-center gap-2">
              {formFinished && (user?.companies?.length || 1) > 1 && (
                <Tooltip
                  enterTouchDelay={0}
                  leaveTouchDelay={5000}
                  title={t('dashboard.topic.forms.formitem.applyothertenants')}
                >
                  <IconButton onClick={() => handleDuplicate()}>
                    <AddBusinessIcon />
                  </IconButton>
                </Tooltip>
              )}
              {(principalCompany?.isDpo || principalCompany?.isAgency) && (
                <Tooltip
                  enterTouchDelay={0}
                  leaveTouchDelay={5000}
                  title={
                    hasCopied
                      ? t('dashboard.topic.process.publishprocess.copied')
                      : t('dashboard.topic.forms.formitem.sharelink')
                  }
                >
                  <IconButton onClick={() => handleShare()} disabled={loading}>
                    {loading ? <Spinner size="small" /> : <ShareIcon />}
                  </IconButton>
                </Tooltip>
              )}
              {formFinished &&
                !isBroken &&
                (isDocument ? (
                  <CtaButton
                    onClick={() => handleButton(index + 1, formFinished)}
                    disabled={formLocked}
                    spinner={dashboard.affects('open', questionnaire?.id)}
                    btnColor="simple"
                  >
                    {CTAMapFinish[app.class]}
                  </CtaButton>
                ) : (
                  <Tooltip enterTouchDelay={0} leaveTouchDelay={5000} title={CTAMapFinish[app.class]}>
                    {dashboard.affects('open', questionnaire?.id) ? (
                      <Spinner size="small" />
                    ) : (
                      <IconButton onClick={() => handleButton(index + 1, formFinished)}>
                        {app.class === ApplicationClass.Training ? <SchoolIcon /> : <EditIcon />}
                      </IconButton>
                    )}
                  </Tooltip>
                ))}

              {formLocked && (
                <Tooltip
                  enterTouchDelay={0}
                  leaveTouchDelay={5000}
                  title={t('dashboard.topic.forms.formitem.firstfinishothers')}
                >
                  <div className="tooltipAnchor" />
                </Tooltip>
              )}
              {isLocked && !isPublic && (
                <Tooltip enterTouchDelay={0} leaveTouchDelay={5000} title={TOPIC_LOCKED_TEXT}>
                  <LockIcon className="text-secondary-600" fontSize="small" />
                </Tooltip>
              )}

              {
                <CtaButton
                  onClick={formFinished && !isBroken ? handleShow : () => handleButton(index + 1, formFinished)}
                  disabled={formLocked}
                  spinner={dashboard.isBusy && !formLocked}
                  data-testid={'button-open-form-' + app.id + '-' + (index + 1)}
                  btnColor={formFinished ? 'simple' : 'primary'}
                  // icon={topicLocked ? <LockIcon className="text-secondary-600" fontSize="small" /> : undefined}
                >
                  {false
                    ? t('common.unlock')
                    : formFinished
                      ? isBroken
                        ? CTAMapRework[app.class]
                        : CTAMapShow[app.class]
                      : revisit
                        ? t('common.revisit')
                        : inProgress
                          ? t('common.proceed')
                          : CTAMapStart[app.class]}
                </CtaButton>
              }
            </div>
            <DuplicateModal open={duplicateModalOpen} onClose={() => setDuplicateModalOpen(false)} app={app} />
          </div>
        }
      />
      <DocumentMenu anchorEl={anchorEl} handleClose={() => setAnchorEl(null)} app={app} onShow={openDocument} />
    </div>
  );
}

import { useUserValuesData } from '@app/hooks/useUserValuesData';
import { useUserWithCompany } from '@app/hooks/useUserWithCompany';
import { copyToClipboard } from '@app/utils/copyToClipboard';
import { useQuestionnaireDocumentShareQuery } from '@dieterApi/questionnaire/useQuestionnaireDocumentShareQuery';

